import type { BillingAddressResource, InvoicingResponse } from '@olxeu-a2b/core/typings/components';
import isEmpty from 'lodash/isEmpty';
import { USER_TYPE, SERVICE_POINT_OPERATOR, FIELD_NAMES } from '../../constants';
import type { AddressState, FormInputEntry, FormInputPayload } from '../order-store/order-store';
import inputDefinitions from './invoice-definitions';

const {
  pocztaBusinessFields,
  inpostAndRuchPrivateFields,
  pocztaPrivateFields,
  allRequiredBusinessFields,
  allRequiredPrivateFields,
  confirmationHiddenBusinessFields,
  confirmationHiddenPrivateFields,
  sellerConfirmationBusinessFields,
  sellerConfirmationPrivateFields,
} = inputDefinitions;

const isBusiness = (type?: string) => type === USER_TYPE.BUSINESS;
const isPrivate = (type?: string) => type === USER_TYPE.PRIVATE;
const isInpost = (operator?: string) => operator === SERVICE_POINT_OPERATOR.inpost;
const isRuch = (operator: string) => operator === SERVICE_POINT_OPERATOR.ruch;
const isPoczta = (operator: string) => operator === SERVICE_POINT_OPERATOR.poczta;
const isInpostOrRuch = (operator: string) => isInpost(operator) || isRuch(operator);

const getValidFields = (
  addressState: AddressState,
  fields: string[],
  operator?: string,
): FormInputPayload[] => {
  const validFields = Object.values(addressState).reduce(
    (acc: FormInputEntry[], input: FormInputEntry) => {
      if (input?.input && fields.includes(input?.input) && input.valid && input.value) {
        return [...acc, input];
      }

      return acc;
    },
    [],
  );

  let numberOfReferenceFields = fields.length;
  if (
    !validFields.some(field => field.input === FIELD_NAMES.apartmentNumber) &&
    (!operator || !isInpostOrRuch(operator))
  ) {
    numberOfReferenceFields -= 1;
  }

  return numberOfReferenceFields === validFields.length ? (validFields as FormInputPayload[]) : [];
};

const getFieldsToPreFillInvoiceForm = (
  addressState: AddressState,
  operator: string,
  type: string,
): FormInputPayload[] => {
  if (!operator || !type) return [];

  const getValidFieldsByOperator = (fields: string[]): FormInputPayload[] =>
    getValidFields(addressState, fields, operator);

  if (isBusiness(type)) {
    if (isInpostOrRuch(operator)) return [];
    if (isPoczta(operator)) return getValidFieldsByOperator(pocztaBusinessFields);
  }

  if (isPrivate(type)) {
    if (isInpostOrRuch(operator)) return getValidFieldsByOperator(inpostAndRuchPrivateFields);
    if (isPoczta(operator)) return getValidFieldsByOperator(pocztaPrivateFields);
  }

  return [];
};

const hasAllRequiredFields = (
  latestBillingInfo: BillingAddressResource,
  userType?: string,
): boolean => {
  if (!latestBillingInfo || !userType) return false;

  const requiredFields = isBusiness(userType)
    ? allRequiredBusinessFields
    : allRequiredPrivateFields;
  return (
    latestBillingInfo?.attributes &&
    requiredFields.every(fieldName => Object.keys(latestBillingInfo.attributes).includes(fieldName))
  );
};

const getAddressFormDataToPrefillInvoiceForm = (
  addressState: AddressState,
  invoiceType: string,
): FormInputPayload[] => {
  const fields = isBusiness(invoiceType)
    ? sellerConfirmationBusinessFields
    : sellerConfirmationPrivateFields;

  return getValidFields(addressState, fields);
};

const generatePrefilledFieldsData = (
  latestBillingInfo: BillingAddressResource,
  userInvoicingInformation: InvoicingResponse['data'],
  hasPreviousBillingInfoBusiness: boolean,
  hasPreviousBillingInfoPrivate: boolean,
  hasMandatoryInvoice: boolean,
): Record<string, string> => {
  let preFillData = {};
  if (!userInvoicingInformation) return preFillData;

  if (hasMandatoryInvoice && userInvoicingInformation) {
    preFillData = {
      ...preFillData,
      [FIELD_NAMES.companyName]: userInvoicingInformation.companyName,
      [FIELD_NAMES.vatNumber]: userInvoicingInformation.taxId,
    };
  }

  if (latestBillingInfo) {
    const { attributes } = latestBillingInfo;
    if (hasPreviousBillingInfoPrivate) {
      preFillData = {
        ...preFillData,
        [FIELD_NAMES.firstName]: attributes.firstName,
        [FIELD_NAMES.lastName]: attributes.lastName,
      };
    }

    if (hasPreviousBillingInfoBusiness || hasPreviousBillingInfoPrivate) {
      preFillData = {
        ...preFillData,
        [FIELD_NAMES.streetName]: attributes.streetName,
        [FIELD_NAMES.streetNumber]: attributes.streetNumber,
        [FIELD_NAMES.apartmentNumber]: attributes?.apartmentNumber,
        [FIELD_NAMES.postCode]: attributes.postCode,
        [FIELD_NAMES.city]: attributes.city,
        [FIELD_NAMES.countryCode]: attributes.countryCode,
      };
    }
  }
  return preFillData;
};

const generatePrefilledFields = (
  latestBillingInfo: BillingAddressResource,
  userInvoicingInformation: InvoicingResponse['data'],
  hasPreviousBillingInfoBusiness: boolean,
  hasPreviousBillingInfoPrivate: boolean,
  hasMandatoryInvoice: boolean,
): FormInputPayload[] => {
  const preFillData = generatePrefilledFieldsData(
    latestBillingInfo,
    userInvoicingInformation,
    hasPreviousBillingInfoBusiness,
    hasPreviousBillingInfoPrivate,
    hasMandatoryInvoice,
  );
  if (isEmpty(preFillData)) return [];

  return Object.keys(preFillData).reduce(
    (result: FormInputPayload[], fieldName: string) =>
      preFillData[fieldName]
        ? [
          ...result,
          {
            input: fieldName,
            value: preFillData[fieldName],
            valid: true,
          },
        ]
        : result,
    [],
  );
};

const getConfirmationInvoiceDisabledFields = (
  hasMandatoryInvoice: boolean,
  userInvoicingInformation: InvoicingResponse['data'],
): string[] =>
  hasMandatoryInvoice && userInvoicingInformation?.companyName && userInvoicingInformation?.taxId
    ? [FIELD_NAMES.companyName, FIELD_NAMES.vatNumber, FIELD_NAMES.countryCode]
    : [FIELD_NAMES.countryCode];

const getConfirmationHiddenFields = (isChecked: boolean, invoiceType: string): string[] => {
  if (!isChecked) {
    return [];
  }

  return isBusiness(invoiceType)
    ? confirmationHiddenBusinessFields
    : confirmationHiddenPrivateFields;
};

const formatTrackingKey = (formName: string, fieldName: string): string =>
  `${formName}_${fieldName}`;

const formatInvoiceFormName = (type: string): string => `invoice-${String(type).toLowerCase()}`;

const getInputName = (name: string, type: string): string =>
  `input[name="${name}-${formatInvoiceFormName(type)}"]`;
const getErrorId = (name: string, type: string): string =>
  `#${name}-${formatInvoiceFormName(type)}-error`;

export default {
  isBusiness,
  isPrivate,
  isInpost,
  isPoczta,
  isRuch,
  isInpostOrRuch,
  getFieldsToPreFillInvoiceForm,
  getAddressFormDataToPrefillInvoiceForm,
  generatePrefilledFieldsData,
  generatePrefilledFields,
  getConfirmationInvoiceDisabledFields,
  formatTrackingKey,
  formatInvoiceFormName,
  getInputName,
  getErrorId,
  hasAllRequiredFields,
  getConfirmationHiddenFields,
};
